import { useEffect, useRef } from "react";
import { isRouteErrorResponse, useRevalidator, useRouteError } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { CenteredMessage, NotFound } from "./NotFound";

/**
 * The staff app's route error boundary.
 *
 * A loader that threw a 404 means the thing does not exist — that is the
 * not-found copy, not a scary error. A loader that failed because the server
 * said nothing is not an error either: AuthProvider is already retrying, so
 * this shows the same "retrying" message App would, and re-runs the loader
 * the moment the session comes back (§3.3 — a deploy must not read as a crash).
 */
export function ErrorScreen() {
  const error = useRouteError();
  const { status } = useAuth();
  const revalidator = useRevalidator();

  // Retry the loader once, and only if this boundary appeared while the
  // session was still unknown or unreachable. A boundary that mounted with a
  // live session is showing a genuine error; retrying that would loop.
  const retryWhenBack = useRef(status !== "authenticated");
  useEffect(() => {
    if (status === "authenticated" && retryWhenBack.current) {
      retryWhenBack.current = false;
      revalidator.revalidate();
    }
  }, [status, revalidator]);

  if (isRouteErrorResponse(error) && error.status === 404) return <NotFound />;
  if (status === "unreachable" || status === "loading") {
    return <CenteredMessage title="Can't reach the server" body="Retrying automatically. Your session is still valid." />;
  }
  const detail = error instanceof Error ? error.message : "Something went wrong rendering this page.";
  return <CenteredMessage title="Something went wrong" body={detail} />;
}
